import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/hooks/useToast';
import { ToastContainer } from '@/components/common/Toast';
import { Layout } from '@/components/layout/Layout';
import { Login } from '@/pages/Login';
import { Dashboard } from '@/pages/Dashboard';
import { Products } from '@/pages/Products';
import { Inventory } from '@/pages/Inventory';
import { Locations } from '@/pages/Locations';
import { Sales } from '@/pages/Sales';
import { Transfers } from '@/pages/Transfers';
import { Settings } from '@/pages/Settings';
import { Supplies } from '@/pages/Supplies';
import { Production } from '@/pages/Production';
import { AuditLog } from '@/pages/AuditLog';
import { StorageLocations } from '@/pages/StorageLocations';
import { ProductDetail } from '@/pages/ProductDetail';
import { LocationDetail } from '@/pages/LocationDetail';
import { SupplyDetail } from '@/pages/SupplyDetail';
import { StorageLocationDetail } from '@/pages/StorageLocationDetail';
import { MediaLibrary } from '@/pages/MediaLibrary';
import { authErrorEvent, isTokenExpired, performLogout } from '@/api/client';
import { getAuthToken } from '@/api/authApi';

function App() {
  const { isAuthenticated, user } = useAuthStore();
  const { toasts, removeToast, error: showError } = useToast();

  // Subscribe to auth error events for toast notifications
  useEffect(() => {
    const unsubscribe = authErrorEvent.subscribe((message) => {
      showError('Session Expired', message);
    });
    return () => { unsubscribe(); };
  }, [showError]);

  // Periodic token validation (check every minute)
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkToken = () => {
      const token = getAuthToken();
      if (token && isTokenExpired(token)) {
        performLogout('Your session has expired. Please log in again.');
      }
    };

    // Check immediately on mount
    checkToken();

    // Check every minute
    const interval = setInterval(checkToken, 60000);
    
    return () => { clearInterval(interval); };
  }, [isAuthenticated]);

  // Handle visibility change (when user returns to tab)
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const token = getAuthToken();
        if (token && isTokenExpired(token)) {
          performLogout('Your session has expired. Please log in again.');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated]);

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" /> : <Login />}
        />
        <Route
          path="/"
          element={isAuthenticated ? <Layout /> : <Navigate to="/login" />}
        >
          <Route index element={<Dashboard />} />
          <Route path="products" element={<Products />} />
          <Route path="products/:id" element={<ProductDetail />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="locations" element={<Locations />} />
          <Route path="locations/:id" element={<LocationDetail />} />
          <Route path="categories" element={<Navigate to="/settings?tab=catalog&section=design-families" replace />} />
          <Route path="catalog/item-types" element={<Navigate to="/settings?tab=catalog&section=item-types" replace />} />
          <Route path="catalog/collections" element={<Navigate to="/settings?tab=catalog&section=collections" replace />} />
          <Route path="catalog/production-methods" element={<Navigate to="/settings?tab=catalog&section=production-methods" replace />} />
          <Route path="sales" element={<Sales />} />
          <Route path="transfers" element={<Transfers />} />
          <Route path="settings" element={<Settings />} />
          <Route path="supplies" element={<Supplies />} />
          <Route path="supplies/:id" element={<SupplyDetail />} />
          <Route path="storage-locations" element={<StorageLocations />} />
          <Route path="storage-locations/:id" element={<StorageLocationDetail />} />
          <Route path="production" element={<Production />} />
          <Route path="audit-log" element={<AuditLog />} />
          <Route path="admin/media" element={user?.role === 'ADMIN' ? <MediaLibrary /> : <Navigate to="/" replace />} />
        </Route>
      </Routes>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </>
  );
}

export default App;
